import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import InlineChatCompose from "./InlineChatCompose";
import { useChatBridge } from "./useChatBridge";
import { requestContextualChatReply } from "./contextual-chat";
import { Badge, Button } from "../components";
import { emitUxTelemetry } from "../telemetry/uxTelemetry";
import { buildWorkPanelDigestText, opensWorkspaceNodeOnPrimaryClick } from "./workspaceCollections";
import { buildBridgeContextSummary } from "./chat/bridgeContext";
import {
  buildSelectedTargetFromWorkspaceNode,
  buildSelectedTargetProvenance,
} from "./selectedTarget";
import { ROUTE_MANIFEST } from "../route-manifest";
import "./workspace-shell.css";

// Static navigation sections — derived from the same ROUTE_MANIFEST the
// Cmd+K palette uses. Admin pages are intentionally omitted from the
// sidebar (operator-secondary; reachable via Cmd+K only).
const SIDEBAR_NAV_SECTIONS: Array<{ heading: string; section: "Navigate" | "Insights" }> = [
  { heading: "Surfaces", section: "Navigate" },
  { heading: "Insights", section: "Insights" },
];

// ---- Types ----

type WorkspaceNodeType =
  | "category"
  | "app"
  | "project"
  | "action_group"
  | "file"
  | "life"
  | "workstream"
  | "issue";

interface WorkspaceNodeMetadata {
  sessionId?: string;
  fileType?: string;
  source?: string;
  fileSize?: string;
  status?: string;
  priority?: string;
  owner?: string;
  progress?: number;
  due?: string;
  [key: string]: unknown;
}

export interface WorkspaceNode {
  id: string;
  type: WorkspaceNodeType;
  label: string;
  icon?: string;
  children?: WorkspaceNode[];
  badge?: { count: number; color: string };
  metadata?: WorkspaceNodeMetadata;
  active?: boolean;
}

interface WorkPanelProps {
  nodes: WorkspaceNode[];
  collapsed: boolean;
  onNodeClick?: (node: WorkspaceNode) => void;
  onNodeSelect?: (node: WorkspaceNode) => void;
  onExpand?: () => void;
  onSettingsClick?: () => void;
  onNewConversation?: () => void;
}

interface WorkspaceDigest {
  totalNodes: number;
  workstreams: number;
  issues: number;
  openIssues: number;
  urgentIssues: number;
  projects: number;
  apps: number;
  files: number;
}

interface ContextualPaneResult {
  assistantReply?: string;
  acknowledgment?: string;
  closeAfterSend?: boolean;
}

function summarizeWorkspace(nodes: WorkspaceNode[]): WorkspaceDigest {
  const digest: WorkspaceDigest = {
    totalNodes: 0,
    workstreams: 0,
    issues: 0,
    openIssues: 0,
    urgentIssues: 0,
    projects: 0,
    apps: 0,
    files: 0,
  };

  const walk = (items: WorkspaceNode[]) => {
    for (const item of items) {
      digest.totalNodes++;
      if (item.type === "workstream") {
        digest.workstreams++;
      }
      if (item.type === "issue") {
        digest.issues++;
        const status = String(item.metadata?.status ?? "open").toLowerCase();
        const priority = String(item.metadata?.priority ?? "").toLowerCase();
        if (!["closed", "resolved", "done", "archived"].includes(status)) {
          digest.openIssues++;
        }
        if (priority === "critical" || priority === "high") {
          digest.urgentIssues++;
        }
      }
      if (item.type === "project") {
        digest.projects++;
      }
      if (item.type === "app") {
        digest.apps++;
      }
      if (item.type === "file") {
        digest.files++;
      }
      if (item.children?.length) {
        walk(item.children);
      }
    }
  };

  walk(nodes);
  return digest;
}

// ---- Static default categories (Phase 1 — replaced by API-driven in Phase 5) ----

export const DEFAULT_WORKSPACE_NODES: WorkspaceNode[] = [
  { id: "life", type: "life" as const, label: "Life", children: [] },
  { id: "conversations", type: "category", label: "Conversations", children: [] },
  { id: "projects", type: "category", label: "Projects", children: [] },
  { id: "workstreams", type: "category", label: "Workstreams", children: [] },
  { id: "apps", type: "category", label: "Apps", children: [] },
  { id: "files", type: "category", label: "Files", children: [] },
];

// ---- SVG icon mapping (replaces emoji) ----

function FileTypeIcon({ fileType, size = 14 }: { fileType: string; size?: number }) {
  const s = size;
  switch (fileType) {
    case "markdown":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 1h5l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1z" />
          <path d="M9 1v4h4" />
          <path d="M5.5 8.5l1.5 2 1.5-2M10 8.5v3" strokeWidth="1.2" />
        </svg>
      );
    case "pdf":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 1h5l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1z" />
          <path d="M9 1v4h4" />
          <rect x="5" y="8" width="6" height="4" rx="0.5" strokeWidth="1" fill="none" />
        </svg>
      );
    case "image":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="2" width="12" height="12" rx="2" />
          <circle cx="5.5" cy="5.5" r="1.5" strokeWidth="1" />
          <path d="M2 11l3-3 2 2 3-3 4 4" strokeWidth="1.2" />
        </svg>
      );
    case "spreadsheet":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="2" width="12" height="12" rx="1.5" />
          <path d="M2 6h12M2 10h12M6 2v12M10 2v12" strokeWidth="1" />
        </svg>
      );
    case "code":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 1h5l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1z" />
          <path d="M9 1v4h4" />
          <path d="M6.5 8.5L5 10l1.5 1.5M9.5 8.5L11 10l-1.5 1.5" strokeWidth="1.2" />
        </svg>
      );
    case "data":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 1h5l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1z" />
          <path d="M9 1v4h4" />
          <path d="M6 9.5c0-1 .5-1 1-1s1 0 1 1-.5 1-1 1-1 0-1-1z" strokeWidth="1" />
        </svg>
      );
    case "archive":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="2" width="12" height="4" rx="1" />
          <path d="M3 6v7a1 1 0 001 1h8a1 1 0 001-1V6" />
          <path d="M6 9h4" />
        </svg>
      );
    default: // text / generic
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 1h5l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1z" />
          <path d="M9 1v4h4" />
          <path d="M6 8h4M6 10h3" strokeWidth="1" />
        </svg>
      );
  }
}

function NodeIcon({ node, size = 14 }: { node: WorkspaceNode; size?: number }) {
  const s = size;

  // File nodes — use file-type-specific icons
  if (node.type === "file" || node.id.startsWith("file-")) {
    const fileType = (node.metadata?.fileType as string) ?? "text";
    return <FileTypeIcon fileType={fileType} size={s} />;
  }

  // Map specific well-known category IDs to distinct icons
  switch (node.id) {
    case "conversations":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M2 3h12v8H6l-3 2.5V11H2V3z" />
        </svg>
      );
    case "projects":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M2 2h5l2 2h5v10H2V2z" />
        </svg>
      );
    case "apps":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="2" width="5" height="5" rx="1" />
          <rect x="9" y="2" width="5" height="5" rx="1" />
          <rect x="2" y="9" width="5" height="5" rx="1" />
          <rect x="9" y="9" width="5" height="5" rx="1" />
        </svg>
      );
    case "files":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M4 1h5l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1z" />
          <path d="M9 1v4h4" />
        </svg>
      );
    case "workstreams":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="4" cy="4" r="1.5" />
          <circle cx="12" cy="4" r="1.5" />
          <circle cx="8" cy="12" r="1.5" />
          <path d="M5.4 4h5.2M4.8 5.2l2.4 5.6M11.2 5.2l-2.4 5.6" />
        </svg>
      );
    case "life":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="6" />
          <path d="M8 4v4.5l3 1.5" />
        </svg>
      );
  }

  // Conversation nodes — show chat bubble
  if (node.id.startsWith("conv-")) {
    return (
      <svg
        width={s}
        height={s}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M3 3h10v7H7l-2.5 2V10H3V3z" />
      </svg>
    );
  }

  // Fall back to type-based icons
  switch (node.type) {
    case "app":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="2" y="2" width="12" height="12" rx="2" />
          <path d="M6 6h4M6 8h4M6 10h2" />
        </svg>
      );
    case "project":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "workstream":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M2.5 4h4M9.5 4h4M4.5 8h7M2.5 12h4M9.5 12h4" />
          <circle cx="2.5" cy="4" r=".8" fill="currentColor" stroke="none" />
          <circle cx="13.5" cy="4" r=".8" fill="currentColor" stroke="none" />
          <circle cx="8" cy="8" r=".8" fill="currentColor" stroke="none" />
          <circle cx="2.5" cy="12" r=".8" fill="currentColor" stroke="none" />
          <circle cx="13.5" cy="12" r=".8" fill="currentColor" stroke="none" />
        </svg>
      );
    case "issue":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="6" />
          <path d="M8 4.5v4M8 10.8v.7" />
        </svg>
      );
    case "action_group":
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <circle cx="8" cy="8" r="6" />
          <path d="M8 5v3.5l2.5 1.5" />
        </svg>
      );
    default:
      return (
        <svg
          width={s}
          height={s}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M2 2h5l2 2h5v10H2V2z" />
        </svg>
      );
  }
}

// ---- Component ----

export default function WorkPanel({
  nodes,
  collapsed,
  onNodeClick,
  onNodeSelect,
  onExpand,
  onSettingsClick,
  onNewConversation,
}: WorkPanelProps): React.ReactElement {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    new Set(["conversations", "life", "projects", "workstreams", "ws-revenue-ops", "apps"]),
  );
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [chatNodeId, setChatNodeId] = useState<string | null>(null);
  const { sendChatMessage } = useChatBridge();
  const navigate = useNavigate();
  const workspaceDigest = useMemo(() => summarizeWorkspace(nodes), [nodes]);
  const renderedNodeIds = useRef(new Set<string>());

  useEffect(() => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      let changed = false;

      const walk = (items: WorkspaceNode[]) => {
        for (const item of items) {
          if (item.type === "workstream") {
            const hasIssueChildren = Boolean(
              item.children?.some((child) => child.type === "issue"),
            );
            if (hasIssueChildren && !next.has(item.id)) {
              next.add(item.id);
              changed = true;
            }
          }
          if (item.children?.length) {
            walk(item.children);
          }
        }
      };

      walk(nodes);
      return changed ? next : prev;
    });
  }, [nodes]);

  useEffect(() => {
    const walk = (items: WorkspaceNode[]) => {
      for (const item of items) {
        if (!renderedNodeIds.current.has(item.id)) {
          renderedNodeIds.current.add(item.id);
          emitUxTelemetry("ux.agent_element.rendered", {
            elementType: "workspace_node",
            elementId: item.id,
          });
        }
        if (item.children?.length) {
          walk(item.children);
        }
      }
    };
    walk(nodes);
  }, [nodes]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleNodeContextMenu = useCallback((node: WorkspaceNode) => {
    setChatNodeId((prev) => (prev === node.id ? null : node.id));
  }, []);

  const requestSidebarReply = useCallback(
    async (node: WorkspaceNode, text: string): Promise<ContextualPaneResult> => {
      const selectedTarget = buildSelectedTargetFromWorkspaceNode(node, "sidebar");
      const userPrompt = [`Sidebar node: ${node.label}`, "", `User request: ${text}`].join("\n");

      const reply = await requestContextualChatReply({
        systemPrompt:
          "You are the user's agent in a contextual sidebar micro-chat. Reply in 1-2 concise sentences, stay focused on this node, and ask one clarifying question when needed.",
        userPrompt,
        surface: "sidebar_contextual_chat",
        contextSummary: node.label,
        uiContext: {
          sidebarNodeId: node.id,
          sidebarNodeLabel: node.label,
          selectedTarget,
        },
        provenance: buildSelectedTargetProvenance(selectedTarget),
      });
      if (reply?.assistantReply) {
        return { assistantReply: reply.assistantReply, closeAfterSend: false };
      }

      return {
        assistantReply: `Understood. I will keep this focused on ${node.label}. What should I do next?`,
        closeAfterSend: false,
      };
    },
    [],
  );

  const handleSelectNode = useCallback(
    (node: WorkspaceNode) => {
      setSelectedNodeId(node.id);
      onNodeSelect?.(node);
    },
    [onNodeSelect],
  );

  if (collapsed) {
    return (
      <aside className="work-panel work-panel--collapsed" aria-label="Collapsed workspace panel">
        {nodes.map((node) => (
          <button
            key={node.id}
            onClick={() => onExpand?.()}
            className="work-panel__collapsed-icon-btn"
            title={node.label}
          >
            <NodeIcon node={node} size={16} />
            {node.badge && node.badge.count > 0 && (
              <span className="work-panel__collapsed-badge">{node.badge.count}</span>
            )}
          </button>
        ))}
        {/* Settings */}
        <button
          onClick={onSettingsClick}
          className="work-panel__collapsed-icon-btn work-panel__collapsed-icon-btn--settings"
          title="Settings"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M6.8 1h2.4l.4 2.2a5.5 5.5 0 011.4.8l2.1-.8 1.2 2.1-1.7 1.4a5.5 5.5 0 010 1.6l1.7 1.4-1.2 2.1-2.1-.8a5.5 5.5 0 01-1.4.8L9.2 15H6.8l-.4-2.2a5.5 5.5 0 01-1.4-.8l-2.1.8-1.2-2.1 1.7-1.4a5.5 5.5 0 010-1.6L1.7 6.3l1.2-2.1 2.1.8a5.5 5.5 0 011.4-.8L6.8 1z" />
            <circle cx="8" cy="8" r="2" />
          </svg>
        </button>
      </aside>
    );
  }

  return (
    <aside className="work-panel" aria-label="Workspace panel">
      <div className="work-panel__header">
        <div className="work-panel__kicker">Workspace</div>
        <Button
          onClick={onNewConversation}
          variant="primary"
          size="sm"
          className="work-panel__new-chat-btn"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M7 3v8M3 7h8" />
          </svg>
          New conversation
        </Button>
        <div className="work-panel__digest">
          <span>
            {buildWorkPanelDigestText({
              workstreams: workspaceDigest.workstreams,
              openIssues: workspaceDigest.openIssues,
            })}
          </span>
          {workspaceDigest.urgentIssues > 0 && (
            <Badge color="red">{workspaceDigest.urgentIssues} urgent</Badge>
          )}
        </div>
      </div>

      {/* Static navigation — Surfaces (organizer) + Insights (trust). Same
          source-of-truth as the Cmd+K palette. Admin pages are intentionally
          omitted; they are reachable via Cmd+K only. */}
      <div className="work-panel__nav">
        {SIDEBAR_NAV_SECTIONS.map(({ heading, section }) => {
          const entries = ROUTE_MANIFEST.filter((entry) => entry.section === section);
          if (entries.length === 0) return null;
          return (
            <div key={section} className="work-panel__nav-section">
              <div className="work-panel__nav-heading">{heading}</div>
              <div className="work-panel__nav-list">
                {entries.map((entry) => (
                  <button
                    key={entry.path}
                    onClick={() => navigate(entry.path)}
                    className="work-panel__nav-row"
                    title={entry.hint ?? entry.label}
                  >
                    <span className="work-panel__nav-label">{entry.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Nodes tree */}
      <nav className="work-panel__tree">
        {nodes.map((node) => (
          <NodeItem
            key={node.id}
            node={node}
            depth={0}
            expanded={expandedIds.has(node.id)}
            onToggle={() => toggleExpand(node.id)}
            onClick={() => onNodeClick?.(node)}
            onSelect={handleSelectNode}
            selectedNodeId={selectedNodeId}
            expandedIds={expandedIds}
            onToggleChild={(id) => toggleExpand(id)}
            onChildClick={onNodeClick}
            chatNodeId={chatNodeId}
            onContextChat={handleNodeContextMenu}
            onChatSend={async (text, node) => {
              const selectedTarget = buildSelectedTargetFromWorkspaceNode(node, "sidebar");
              const bridgeContext = {
                sidebarNodeId: node.id,
                sidebarNodeLabel: node.label,
                workspaceDigestSummary: buildWorkPanelDigestText({
                  workstreams: workspaceDigest.workstreams,
                  openIssues: workspaceDigest.openIssues,
                }),
                ...(selectedTarget ? { selectedTarget } : {}),
              };
              sendChatMessage(text, {
                ...bridgeContext,
                contextSummary: buildBridgeContextSummary(bridgeContext),
              });
              return requestSidebarReply(node, text);
            }}
            onChatClose={() => setChatNodeId(null)}
          />
        ))}
      </nav>

      {/* Settings button — always at the bottom */}
      <div className="work-panel__footer">
        <Button
          onClick={onSettingsClick}
          variant="ghost"
          size="sm"
          className="work-panel__settings-btn"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M6.8 1h2.4l.4 2.2a5.5 5.5 0 011.4.8l2.1-.8 1.2 2.1-1.7 1.4a5.5 5.5 0 010 1.6l1.7 1.4-1.2 2.1-2.1-.8a5.5 5.5 0 01-1.4.8L9.2 15H6.8l-.4-2.2a5.5 5.5 0 01-1.4-.8l-2.1.8-1.2-2.1 1.7-1.4a5.5 5.5 0 010-1.6L1.7 6.3l1.2-2.1 2.1.8a5.5 5.5 0 011.4-.8L6.8 1z" />
            <circle cx="8" cy="8" r="2" />
          </svg>
          Settings
        </Button>
      </div>
    </aside>
  );
}

// ---- Tree node renderer ----

function NodeItem({
  node,
  depth,
  expanded,
  onToggle,
  onClick,
  onSelect,
  selectedNodeId,
  expandedIds,
  onToggleChild,
  onChildClick,
  chatNodeId,
  onContextChat,
  onChatSend,
  onChatClose,
}: {
  node: WorkspaceNode;
  depth: number;
  expanded: boolean;
  onToggle: () => void;
  onClick: () => void;
  onSelect?: (node: WorkspaceNode) => void;
  selectedNodeId?: string | null;
  expandedIds: Set<string>;
  onToggleChild: (id: string) => void;
  onChildClick?: (node: WorkspaceNode) => void;
  chatNodeId?: string | null;
  onContextChat?: (node: WorkspaceNode) => void;
  onChatSend?: (
    text: string,
    node: WorkspaceNode,
  ) => void | ContextualPaneResult | Promise<void | ContextualPaneResult>;
  onChatClose?: () => void;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isCategory = node.type === "category";
  const isFiles = node.id === "files";
  const isIssue = node.type === "issue";
  const isWorkstream = node.type === "workstream";
  const isLife = node.type === "life" || node.id === "life";
  const opensOnPrimaryClick = opensWorkspaceNodeOnPrimaryClick(node);
  const paddingLeft = 8 + depth * 16;
  const linkedSessionId =
    typeof node.metadata?.sessionId === "string" ? node.metadata.sessionId : undefined;
  const canToggleChildren = Boolean(hasChildren) && !linkedSessionId && !opensOnPrimaryClick;

  const status = typeof node.metadata?.status === "string" ? node.metadata.status : undefined;
  const priority = typeof node.metadata?.priority === "string" ? node.metadata.priority : undefined;
  const owner = typeof node.metadata?.owner === "string" ? node.metadata.owner : undefined;
  const source = typeof node.metadata?.source === "string" ? node.metadata.source : undefined;
  const fileSize = typeof node.metadata?.fileSize === "string" ? node.metadata.fileSize : undefined;
  const projectLabel =
    typeof node.metadata?.projectLabel === "string" ? node.metadata.projectLabel : undefined;
  const conversationLabel =
    typeof node.metadata?.conversationLabel === "string"
      ? node.metadata.conversationLabel
      : undefined;
  const progress =
    typeof node.metadata?.progress === "number"
      ? Math.max(0, Math.min(100, Math.round(node.metadata.progress)))
      : undefined;
  const due = typeof node.metadata?.due === "string" ? node.metadata.due : undefined;
  const isUrgentIssue =
    isIssue && (priority?.toLowerCase() === "critical" || priority?.toLowerCase() === "high");

  const hintSegments: string[] = [];
  if (isFiles && depth === 0) hintSegments.push("Open your files");
  if (node.type === "file") {
    if (projectLabel) hintSegments.push(projectLabel);
    if (conversationLabel) hintSegments.push(conversationLabel);
    if (!projectLabel && fileSize) hintSegments.push(fileSize);
    if (source && hintSegments.length < 2) {
      hintSegments.push(
        source.toLowerCase() === "agent" ? "Generated by Agent" : "Uploaded by you",
      );
    }
  }
  if (status && (isIssue || isWorkstream)) hintSegments.push(status.replace(/_/g, " "));
  if (progress != null && isWorkstream) hintSegments.push(`${progress}% complete`);
  if (priority && isIssue) hintSegments.push(`${priority} priority`);
  if (due && isIssue) hintSegments.push(`due ${due}`);
  if (owner && (isIssue || isWorkstream)) hintSegments.push(owner);
  const hint = hintSegments.slice(0, 2).join(" · ");

  return (
    <div>
      <button
        data-node-id={node.id}
        className={`work-panel__node-row${node.active ? " work-panel__node-row--active" : ""}${
          selectedNodeId === node.id ? " work-panel__node-row--selected" : ""
        }${isUrgentIssue ? " work-panel__node-row--urgent" : ""}`}
        style={{
          paddingLeft,
          paddingRight: 8,
        }}
        data-object-id={
          typeof node.metadata?.objectId === "string" ? node.metadata.objectId : undefined
        }
        data-object-kind={
          typeof node.metadata?.objectKind === "string" ? node.metadata.objectKind : node.type
        }
        data-file-id={typeof node.metadata?.fileId === "string" ? node.metadata.fileId : undefined}
        data-action-id={
          typeof node.metadata?.actionId === "string" ? node.metadata.actionId : undefined
        }
        data-message-id={
          typeof node.metadata?.messageId === "string" ? node.metadata.messageId : undefined
        }
        data-conversation-id={
          typeof node.metadata?.conversationId === "string"
            ? node.metadata.conversationId
            : linkedSessionId
        }
        data-resource-uri={
          typeof node.metadata?.resourceUri === "string" ? node.metadata.resourceUri : undefined
        }
        onClick={(e) => {
          onSelect?.(node);
          if (opensOnPrimaryClick) {
            onClick();
            return;
          }
          if (canToggleChildren) {
            // Preserve accordion intent on double-click by toggling only on the first click.
            if (e.detail > 1) return;
            onToggle();
            return;
          }
          onClick();
        }}
        onDoubleClick={(e) => {
          if (canToggleChildren) {
            // Expand/collapse nodes should reserve double-click for accordion behavior.
            onSelect?.(node);
            return;
          }
          // Double-click on leaf/action nodes opens contextual micro-chat.
          e.preventDefault();
          onSelect?.(node);
          onContextChat?.(node);
        }}
        onContextMenu={(e) => {
          // Right-click on any node opens contextual micro-chat.
          if (onContextChat) {
            e.preventDefault();
            onSelect?.(node);
            onContextChat(node);
          }
        }}
      >
        {canToggleChildren ? (
          <span
            className={`work-panel__chevron${expanded ? " work-panel__chevron--expanded" : ""}`}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4.5 2.5l3.5 3.5-3.5 3.5" />
            </svg>
          </span>
        ) : (
          <span className="work-panel__chevron work-panel__chevron--placeholder" />
        )}

        <span className="work-panel__node-icon" aria-hidden>
          <NodeIcon node={node} size={isCategory || isLife ? 14 : 12} />
        </span>

        <span className="work-panel__node-content">
          <span className="work-panel__node-label">{node.label}</span>
          {hint && <span className="work-panel__node-hint">{hint}</span>}
        </span>

        {/* Badge */}
        {node.badge && node.badge.count > 0 && (
          <span
            className="work-panel__node-badge"
            style={{ backgroundColor: node.badge.color || undefined }}
          >
            {node.badge.count}
          </span>
        )}
      </button>

      {/* Inline chat (right-click to chat about this node) */}
      {chatNodeId === node.id && onChatSend && onChatClose && (
        <div className="work-panel__inline-chat">
          <InlineChatCompose
            label={node.label}
            contextId={node.id}
            contextType="sidebar"
            onSend={(text) => onChatSend(text, node)}
            onClose={onChatClose}
          />
        </div>
      )}

      {/* Children */}
      {hasChildren && expanded && !isFiles && (
        <div>
          {node.children!.map((child) => (
            <NodeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expandedIds.has(child.id)}
              onToggle={() => onToggleChild(child.id)}
              onClick={() => onChildClick?.(child)}
              onSelect={onSelect}
              selectedNodeId={selectedNodeId}
              expandedIds={expandedIds}
              onToggleChild={onToggleChild}
              onChildClick={onChildClick}
              chatNodeId={chatNodeId}
              onContextChat={onContextChat}
              onChatSend={onChatSend}
              onChatClose={onChatClose}
            />
          ))}
        </div>
      )}
    </div>
  );
}
